import { useMemo } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import api from '@/services/api'
import type {
  CalibrationRecord,
  ConfigChange,
  JointOrigin,
  RobotEvent,
  RobotEventsResponse,
  TaskRun,
  TaskRunError,
  TaskRunStep,
} from '@/types/robotEvents'

// 機体イベントジャーナルを時間範囲で取得し、run_start〜run_end 単位の
// 「実行」にグルーピングして返す (詳細画面のタスク実行表示用)。
// 旧ブリッジの機体はイベント自体が無いので runs は空になる。
export function useRobotEvents(
  forkId: string | undefined,
  start: Date | undefined,
  end: Date | undefined
) {
  const startIso = start?.toISOString()
  const endIso = end?.toISOString()

  const { data, isLoading } = useQuery({
    queryKey: ['robotEvents', forkId, startIso, endIso],
    queryFn: async () => {
      const { data } = await api.get<RobotEventsResponse>('/robot-events', {
        params: { fork_id: forkId, start: startIso, end: endIso },
      })
      return data
    },
    enabled: !!forkId && !!startIso && !!endIso,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 30,
    placeholderData: keepPreviousData,
  })

  const runs = useMemo(() => groupIntoRuns(data?.events ?? []), [data?.events])
  const configChanges = useMemo(
    () => extractConfigChanges(data?.events ?? []),
    [data?.events]
  )
  const calibrations = useMemo(
    () => extractCalibrations(data?.events ?? []),
    [data?.events]
  )

  return {
    runs,
    configChanges,
    calibrations,
    truncated: data?.truncated ?? false,
    isLoading,
  }
}

// 機体の実行時設定の変更履歴 (config_changed) と起動時の再適用 (config_restored) を
// 新しい順に並べる。config_restored は設定ごとに 1 行へ展開する
function extractConfigChanges(events: RobotEvent[]): ConfigChange[] {
  const changes: ConfigChange[] = []
  for (const ev of events) {
    const data = ev.data ?? {}
    const base = `${ev.ts}-${ev.seq ?? ''}`
    if (ev.event_type === 'config_changed') {
      changes.push({
        key: base,
        ts: ev.ts,
        settingKey: str(data.key),
        value: data.value,
        previous: data.previous ?? null,
        source: str(data.source) || 'unknown',
      })
    } else if (ev.event_type === 'config_restored') {
      const settings = (data.settings ?? {}) as Record<string, unknown>
      for (const [settingKey, value] of Object.entries(settings)) {
        changes.push({
          key: `${base}-${settingKey}`,
          ts: ev.ts,
          settingKey,
          value,
          previous: null,
          source: 'restored',
        })
      }
    }
  }
  return changes.reverse()
}

// キャリブレーション画面からの URDF 書き換え (calibration_applied) を新しい順に並べる
function extractCalibrations(events: RobotEvent[]): CalibrationRecord[] {
  const records: CalibrationRecord[] = []
  for (const ev of events) {
    if (ev.event_type !== 'calibration_applied') continue
    const data = ev.data ?? {}
    records.push({
      key: `${ev.ts}-${ev.seq ?? ''}`,
      ts: ev.ts,
      joint: str(data.joint),
      before: jointOrigin(data.before),
      after: jointOrigin(data.after),
      method: str(data.method),
      residual: str(data.residual),
      by: str(data.by),
      path: str(data.path),
      urdf: str(data.urdf),
    })
  }
  return records.reverse()
}

function jointOrigin(v: unknown): JointOrigin | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const vec = (a: unknown) =>
    Array.isArray(a) && a.length === 3 && a.every((n) => typeof n === 'number')
      ? (a as number[])
      : null
  const xyz = vec(o.xyz)
  const rpy = vec(o.rpy)
  return xyz && rpy ? { xyz, rpy } : null
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function scenarioOf(data: Record<string, unknown>) {
  const sc = (data.scenario ?? {}) as Record<string, unknown>
  return {
    scenarioId: str(sc.scenarioId),
    scenarioName: str(sc.scenarioName),
    trigger: str(sc.trigger) || 'unknown',
    executionMode: str(sc.executionMode),
    isPlaybackScenario: sc.isPlaybackScenario === true,
    requestedBy: str(sc.requestedBy) || undefined,
  }
}

function groupIntoRuns(events: RobotEvent[]): TaskRun[] {
  const runs: TaskRun[] = []
  let current: TaskRun | null = null

  const close = (run: TaskRun) => {
    runs.push(run)
  }

  for (const ev of events) {
    const t = ev.event_type
    const data = ev.data ?? {}

    if (t === 'scenario_run_start' || t === 'task_run_start') {
      if (current) close(current) // 範囲内で終了イベントを観測できなかった実行
      current = {
        key: `${ev.ts}-${ev.seq ?? ''}`,
        isScenario: t === 'scenario_run_start',
        ...scenarioOf(data),
        reconstructed: ev.reconstructed === true,
        startTs: ev.ts,
        endTs: null,
        reason: null,
        durationSec: null,
        pausedSec: 0,
        pauseCount: 0,
        path: null,
        tasks: [],
        errors: [],
      }
      const firstTask = data.firstTask as Record<string, unknown> | undefined
      if (firstTask) {
        current.tasks.push({
          ts: ev.ts,
          taskType: str(firstTask.taskType),
          srcId: str(firstTask.srcId),
          dstId: str(firstTask.dstId),
        })
      }
    } else if (t === 'task_start') {
      if (current) {
        const step: TaskRunStep = {
          ts: ev.ts,
          taskType: str(data.taskType),
          srcId: str(data.srcId),
          dstId: str(data.dstId),
        }
        // run_start の firstTask と同一タスクが直後に来るケースを重複排除
        const last = current.tasks[current.tasks.length - 1]
        if (
          !last ||
          last.taskType !== step.taskType ||
          last.dstId !== step.dstId ||
          last.srcId !== step.srcId
        ) {
          current.tasks.push(step)
        }
      }
    } else if (t === 'error_occurred') {
      if (current) {
        const err: TaskRunError = {
          ts: ev.ts,
          code: num(data.code) ?? 0,
          level: num(data.level),
          message: str(data.message),
        }
        current.errors.push(err)
      }
    } else if (t === 'scenario_run_end' || t === 'task_run_end') {
      const run: TaskRun = current ?? {
        // 表示範囲より前に開始した実行 (run_start が範囲外)
        key: `${ev.ts}-end`,
        isScenario: t === 'scenario_run_end',
        ...scenarioOf(data),
        reconstructed: ev.reconstructed === true,
        startTs: null,
        endTs: null,
        reason: null,
        durationSec: null,
        pausedSec: 0,
        pauseCount: 0,
        path: null,
        tasks: [],
        errors: [],
      }
      run.endTs = ev.ts
      run.reason = str(data.reason) || 'completed'
      run.durationSec = num(data.durationSec)
      run.pausedSec = num(data.pausedSec) ?? 0
      run.pauseCount = num(data.pauseCount) ?? 0
      run.path = Array.isArray(data.path)
        ? (data.path as [number, number][])
        : null
      close(run)
      current = null
    }
  }
  if (current) close(current) // 実行中 (または表示範囲で終了未観測)

  return runs
}
